// src/pages/RegistrarGrupo.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import '../styles/formularios.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveApiBaseList } from '../utils/apiBase';

export default function RegistrarGrupo() {
  const navigate = useNavigate();
    const location = useLocation();
    const handleSalir = () => {
      const rol = resolveRolBase(location.pathname);
      if (rol) {
        navigate(`/${rol}/inicio`, { replace: true });
      } else {
        // Fallback por si no se detecta rol ni en path ni en localStorage:
        navigate('/inicio', { replace: true });
      }
    };
  // Form
  const [siguienteId, setSiguienteId] = useState('Auto');
  const [grado, setGrado] = useState('');
  const [cicloEscolar, setCicloEscolar] = useState('');
  const status = 'Activo';

  // Buscador
  const [inputAlumno, setInputAlumno] = useState('');
  const [sugerencias, setSugerencias] = useState([]); // [{IdAlumno, NombreCompleto, CURP}]
  const [loadingSug, setLoadingSug] = useState(false);
  const debounceRef = useRef(null);

  // Seleccionados
  const [alumnosSel, setAlumnosSel] = useState([]); // [{id, nombre, curp}]
  const tablaAlumnosRef = useRef(null);

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // keep raw message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          const data = trimmed ? JSON.parse(trimmed) : {};
          return { data, url };
        }

        lastError = new Error(`Respuesta inválida desde ${url}`);
        console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const sendJsonWithFallback = useCallback(async (path, payload, method = 'POST') => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // keep raw message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la solicitud ${method} a ${url}:`, error);
          continue;
        }

        const trimmed = text.trim();
        if (trimmed && trimmed.startsWith('<')) {
          const error = new Error(`Respuesta inválida desde ${url}`);
          lastError = error;
          console.warn(`⚠️ Contenido no válido desde ${url}`);
          continue;
        }

        if (!trimmed) return { text: '', parsed: null, url };

        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            return { text: parsed?.mensaje || parsed?.message || trimmed, parsed, url };
          } catch (parseErr) {
            console.warn(`⚠️ No se pudo parsear respuesta JSON desde ${url}:`, parseErr);
          }
        }

        return { text: trimmed, parsed: null, url };
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al enviar ${method} a ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  useEffect(() => { AOS.init(); }, []);

  const cargarSiguienteId = useCallback(async () => {
    try {
      const { data } = await fetchJsonWithFallback('/grupos/siguiente-id');
      setSiguienteId(data?.siguienteId ?? 'Auto');
    } catch (error) {
      console.error('❌ Error obteniendo siguiente ID:', error);
      setSiguienteId('Auto');
    }
  }, [fetchJsonWithFallback]);

  useEffect(() => { cargarSiguienteId(); }, [cargarSiguienteId]);

  // Debounce buscador
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!inputAlumno.trim()) { setSugerencias([]); return; }

    debounceRef.current = setTimeout(async () => {
      setLoadingSug(true);
      try {
        const query = `/grupos/buscar-alumnos?q=${encodeURIComponent(inputAlumno.trim())}&limit=15`;
        const { data } = await fetchJsonWithFallback(query);
        setSugerencias(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('❌ Error buscando alumnos:', err);
        setSugerencias([]);
      } finally {
        setLoadingSug(false);
      }
    }, 250);

    return () => clearTimeout(debounceRef.current);
  }, [fetchJsonWithFallback, inputAlumno]);

  function agregarAlumnoSeleccionado(item) {
    const nuevo = { id: item.IdAlumno, nombre: item.NombreCompleto, curp: item.CURP };
    setAlumnosSel(prev => (prev.some(x => x.id === nuevo.id) ? prev : [...prev, nuevo]));
  }

  function quitarAlumno(id) {
    setAlumnosSel(prev => prev.filter(x => x.id !== id));
  }

  // Enter = tomar primer resultado
  function onKeyDownInput(e) {
    if (e.key === 'Enter' && sugerencias.length > 0) {
      e.preventDefault();
      agregarAlumnoSeleccionado(sugerencias[0]);
      setInputAlumno('');
      setSugerencias([]);
    }
  }

  async function validarGrupoExistente(gradoSeleccionado) {
    try {
      const { data } = await fetchJsonWithFallback(`/grupos/por-grado/${gradoSeleccionado}/detalle`);
      return !!data?.grupo;
    } catch (error) {
      console.error('❌ Error al validar existencia del grupo:', error);
      alert('No se pudo validar si ya existe un grupo para ese grado. Intenta nuevamente en unos minutos.');
      return null;
    }
  }

  async function insertarGrupo(e) {
    e.preventDefault();
    if (!grado) return alert('Selecciona un grado.');
    if (!cicloEscolar.trim()) return alert('Escribe el ciclo escolar.');
    if (alumnosSel.length === 0) return alert('Agrega al menos un alumno.');

    const existe = await validarGrupoExistente(Number(grado));
    if (existe) {
      alert('Ese grupo ya está creado, si quiere agregar alumnos al grupo, dé clic en la opción Modificar Grupo y ahí seleccione el grupo.');
      return;
    }
    if (existe === null) return;

    const idsAlumnos = alumnosSel.map(a => a.id);

    try {
      const { parsed, text } = await sendJsonWithFallback('/grupos/registrar', {
        grado: Number(grado),
        status,
        cicloEscolar: cicloEscolar.trim(),
        idsAlumnos,
      });

      if (parsed?.error) {
        throw new Error(parsed.error);
      }

      alert(
        text ||
        `✅ Grupo registrado (Id: ${parsed?.idGrupo ?? 'N/D'}) con ${parsed?.totalAlumnos ?? idsAlumnos.length} alumno(s).`
      );
      setGrado(''); setCicloEscolar(''); setAlumnosSel([]); setInputAlumno(''); setSugerencias([]);
      cargarSiguienteId();
    } catch (err) {
      console.error('❌ Error al registrar el grupo:', err);
      alert(err.message || '❌ Error al registrar el grupo');
    }
  }

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <h4 className="text-center flex-grow-1 mb-0">
          Instituto Silviano Carrillo<br />
          <small className="text-muted">Registrar Grupo</small>
        </h4>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Formulario */}
      <div className="mb-4 p-4 rounded bg-light shadow-sm">
        <h6 className="mb-3">Registro de Grupo</h6>

        <form className="row g-3" id="formGrupo" onSubmit={insertarGrupo}>
          <div className="col-md-2">
            <label htmlFor="idGrupo" className="form-label">ID Grupo</label>
            <input type="text" className="form-control" id="idGrupo" value={siguienteId} disabled />
          </div>

          <div className="col-md-4">
            <label htmlFor="grado" className="form-label">Grado</label>
            <select className="form-select" id="grado" value={grado} onChange={(e) => setGrado(e.target.value)}>
              <option disabled value="">Selecciona un grado</option>
              <option value="1">1° Grado</option>
              <option value="2">2° Grado</option>
              <option value="3">3° Grado</option>
              <option value="4">4° Grado</option>
              <option value="5">5° Grado</option>
              <option value="6">6° Grado</option>
            </select>
          </div>

          <div className="col-md-4">
            <label htmlFor="status" className="form-label">Status</label>
            <input type="text" className="form-control" id="status" value="Activo" disabled />
          </div>

          <div className="col-md-4">
            <label htmlFor="cicloEscolar" className="form-label">Ciclo Escolar</label>
            <input
              type="text"
              className="form-control"
              id="cicloEscolar"
              value={cicloEscolar}
              onChange={(e) => setCicloEscolar(e.target.value)}
              placeholder="Ej. 2024-2025"
            />
          </div>

          {/* Buscador (sin botón Agregar) */}
          <div className="col-12 mt-4">
            <label htmlFor="buscadorAlumno" className="form-label">Agregar Alumno al Grupo</label>

            <div className="position-relative">
              <input
                className="form-control mb-2"
                id="buscadorAlumno"
                placeholder="Buscar alumno por nombre o CURP"
                value={inputAlumno}
                onChange={(e) => setInputAlumno(e.target.value)}
                onKeyDown={onKeyDownInput}
                autoComplete="off"
              />

              {inputAlumno && (loadingSug || sugerencias.length > 0) && (
                <div className="list-group shadow position-absolute w-100" style={{ zIndex: 10, maxHeight: 260, overflowY: 'auto' }}>
                  {loadingSug && <div className="list-group-item text-muted">Buscando…</div>}
                  {!loadingSug && sugerencias.length === 0 && <div className="list-group-item text-muted">Sin resultados</div>}
                  {!loadingSug && sugerencias.map(s => (
                    <button
                      type="button"
                      key={s.IdAlumno}
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                      onClick={() => { agregarAlumnoSeleccionado(s); setInputAlumno(''); setSugerencias([]); }}
                    >
                      <span>{s.NombreCompleto}</span>
                      <span className="badge bg-secondary">{s.CURP}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tabla */}
          <div className="col-12">
            <div className="table-responsive">
              <table className="table table-bordered table-hover shadow-sm bg-white mt-2" id="tablaAlumnosGrupo" ref={tablaAlumnosRef}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '70%' }}>Nombre del Alumno</th>
                    <th>CURP</th>
                    <th style={{ width: 120 }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {alumnosSel.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center text-muted">Sin alumnos agregados</td>
                    </tr>
                  )}
                  {alumnosSel.map(a => (
                    <tr key={a.id}>
                      <td>{a.nombre}</td>
                      <td>{a.curp}</td>
                      <td className="text-center">
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => quitarAlumno(a.id)}>
                          Quitar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          
        </form>
      </div>

      {/* Botones fuera del form */}
      <div className="d-flex justify-content-center gap-4">
        <button
          type="button"
          className="btn btn-success px-4"
          onClick={insertarGrupo}
        >
          Registrar
        </button>
        <button type="button" className="btn btn-danger px-4" onClick={handleSalir}>
          Salir
        </button>

      </div>
    </div>
  );
}

/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  // 1) Intenta desde la URL actual
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  // 2) Respaldo desde localStorage.role (números mapeados a slugs)
  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}